import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";
import { LayoutDashboard, Building2, Palette, PanelsTopLeft, Users2, SlidersHorizontal, Plug, CreditCard, ShieldCheck, LogOut, Search, Bell, Menu, X } from "lucide-react";

// Ordered by the natural tenant SETUP sequence (matches the reference
// Setup Guide flow): identity -> look & feel -> portals -> team ->
// data -> connections -> billing -> security. Overview is the landing.
const TENANT_ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/company", label: "Company Profile", icon: Building2 },
  { to: "/admin/branding", label: "Branding", icon: Palette },
  { to: "/admin/portals", label: "Portals", icon: PanelsTopLeft },
  { to: "/admin/roles", label: "Users & Roles", icon: Users2 },
  { to: "/admin/custom-fields", label: "Custom Fields", icon: SlidersHorizontal },
  { to: "/admin/integrations", label: "Integrations", icon: Plug },
  { to: "/admin/billing", label: "Billing", icon: CreditCard },
  { to: "/admin/security", label: "Security", icon: ShieldCheck },
];

const PLATFORM_NAV = [{ to: "/platform", label: "Platform Dashboard", icon: Building2 }];

function initials(text) {
  if (!text) return "U";
  const parts = String(text).replace(/@.*/, "").split(/[.\s_-]+/).filter(Boolean);
  return (parts[0]?.[0] || "U").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
}

/**
 * EX-04 — a working in-app search. There is no global-search BACKEND in
 * this SPA, so the box is a client-side navigator over the known admin
 * sections: type to filter the nav destinations, ArrowUp/Down to move,
 * Enter or click to navigate, Escape to close. No fake server.
 */
function HeaderSearch({ nav, placeholder }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  const results = query.trim()
    ? nav.filter((n) => n.label.toLowerCase().includes(query.trim().toLowerCase()))
    : nav;

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const go = (item) => {
    if (!item) return;
    navigate(item.to);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative max-w-md flex-1">
      <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          placeholder={placeholder}
          data-testid="app-search"
          aria-label="Search"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
          data-testid="app-search-results"
        >
          {results.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">No matches</p>
          ) : (
            results.map((item, idx) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  key={item.to}
                  role="option"
                  aria-selected={idx === active}
                  onMouseEnter={() => setActive(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => go(item)}
                  className={`flex w-full items-center gap-2.5 rounded-sm px-3 py-2 text-left text-sm outline-none ${
                    idx === active ? "bg-accent text-accent-foreground" : "text-popover-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {item.label}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/**
 * EX-05 — a working notifications bell. There is no notifications BACKEND
 * in this SPA, so the bell toggles a small popover with an honest empty
 * state (no fake unread dot). Click toggles, click-outside and Escape
 * close, keyboard accessible via aria-expanded.
 */
function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        className="relative text-muted-foreground hover:text-foreground"
        aria-label="Notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="app-notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <Bell className="h-5 w-5" />
      </button>
      {open && (
        <div
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-label="Notifications"
          data-testid="app-notifications-panel"
          className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md outline-none"
        >
          <div className="border-b border-border px-4 py-3">
            <p className="text-sm font-semibold">Notifications</p>
          </div>
          <div className="px-4 py-8 text-center">
            <Bell className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">You&rsquo;re all caught up — no new notifications.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children, variant = "tenant" }) {
  const { logout, user } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = variant === "platform" ? PLATFORM_NAV : TENANT_ADMIN_NAV;
  const roleLabel = variant === "platform" ? "Platform Admin" : "Tenant Admin";
  const displayName = user?.email || (variant === "platform" ? "Operator" : "Admin");
  const searchPlaceholder = variant === "platform" ? "Search tenants, users, or settings…" : "Search settings, users…";

  const SidebarInner = (
    <>
      <div className="flex items-center justify-between px-5 py-5">
        <div className="rounded-md bg-white/95 px-2.5 py-1.5">
          <BrandLogo size="sm" />
        </div>
        <span className="hidden text-xs font-medium uppercase tracking-wide text-white/60 lg:block">
          {variant === "platform" ? "Platform" : "Tenant"}
        </span>
        <button
          className="text-white/80 hover:text-white lg:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {nav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/admin" || to === "/platform"}
            onClick={() => setDrawerOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive ? "bg-sidebar-active text-sidebar-active-foreground" : "text-white/85 hover:bg-white/10"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4">
        <Button
          variant="ghost"
          className="w-full justify-start gap-2.5 text-white/85 hover:bg-white/10 hover:text-white"
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen">
      {/* Fixed sidebar (lg+) */}
      <aside className="hidden w-60 shrink-0 flex-col bg-sidebar text-sidebar-foreground lg:flex" data-testid="app-sidebar">
        {SidebarInner}
      </aside>

      {/* Mobile drawer + overlay */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-sidebar text-sidebar-foreground shadow-xl" data-testid="app-sidebar-mobile">
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-border bg-card px-4 sm:px-6" data-testid="app-header">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              className="text-muted-foreground hover:text-foreground lg:hidden"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              data-testid="app-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>
            <HeaderSearch nav={nav} placeholder={searchPlaceholder} />
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <NotificationsBell />
            <div className="flex items-center gap-2.5" data-testid="app-user-chip">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                {initials(displayName)}
              </div>
              <div className="hidden sm:block">
                <p className="max-w-[180px] truncate text-sm font-semibold leading-tight">{displayName}</p>
                <p className="text-xs text-muted-foreground">{roleLabel}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-background p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
